import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useRecoilState } from 'recoil';
import { globalState } from '@/recoil/global';

import { useKeyCapture, catchDash, getRemainingTime } from '@/utils/form';
import { useForm } from 'react-hook-form';
import { ValidationRules } from 'react-hook-form';
import { request } from '@/utils/request';
import { ENDPOINTS } from '@/constants';
import FormInputComponent from '../FormInputComponent';
import ButtonComponent from '../ButtonComponent';
import theme from '@/styles/theme';

export type FormType = 'phone' | 'email';
export type AuthType = 'find_id' | 'join' | 'find_password';
export type AuthInput = {
  input: string;
  authCode: string;
};

interface Props {
  type: FormType;
  authType: AuthType;
  rules: ValidationRules;
  placeholder: string;
  authSuccess: boolean;
  onAuth: (type: FormType, token: string, input: string) => void;
  noLabel?: boolean;
}

function VerificationForm(props: Props) {
  const [global, setGlobal] = useRecoilState(globalState);
  const { type, authSuccess, rules, placeholder, onAuth, noLabel } = props;
  const { handleSubmit, control, errors, watch } = useForm<AuthInput>({
    defaultValues: {
      input: '',
      authCode: ''
    }
  });

  const { input, authCode } = watch();

  const [time, setTime] = useState<number>(1);
  const [clockOn, setClock] = useState<boolean>(false);

  const remainingTime = getRemainingTime(time);
  const [key, captureKey] = useKeyCapture();

  const reqAuthCode = async (userInput: AuthInput) => {
    const { input } = userInput;
    try {
      setGlobal({
        ...global,
        isLoading: true
      });
      await request().post(ENDPOINTS.VERIFICATIONS_TOKEN, {
        type,
        target: input
      });

      setClock(true);
      setTime(300000);
    } catch (e) {
      // globalError.setError(e);
    } finally {
      setGlobal({
        ...global,
        isLoading: false
      });
    }
  };

  useEffect(() => {
    if (!time) alert('다시 시도하세요');
    if (clockOn) {
      if (time > 0) {
        const ticktock = setTimeout(() => {
          setTime(time - 1000);
        }, 1000);
        return () => clearTimeout(ticktock);
      }
    }
  }, [time, clockOn]);

  useEffect(() => {
    if (authSuccess) setClock(false);
    if (!input) {
      setClock(false);
    }
  }, [authSuccess, input]);

  return (
    <Wrapper>
      <FormInputComponent
        keyName={'input'}
        label={noLabel ? '' : '휴대폰'}
        control={control}
        placeholder={placeholder}
        defaultValue={''}
        disabled={authSuccess}
        error={errors.input}
        rightElement={
          <ButtonComponent
            borderColor={theme.DF_COLOR.GREEN500}
            bgColor={theme.DF_COLOR.GREEN500}
            textColor={theme.DF_COLOR.WHITE}
            text={clockOn ? '다시 받기' : '인증번호 받기'}
            onClick={handleSubmit(reqAuthCode)}
            disabled={!input || authSuccess}
          />
        }
        onKeyDownCapture={(e: any) => captureKey(e)}
        onChange={type === 'phone' && catchDash(key)}
        rules={rules}>
        {clockOn && remainingTime}
      </FormInputComponent>
      <FormInputComponent
        keyName="authCode"
        label={noLabel ? '' : ' '}
        type="number"
        control={control}
        placeholder={'인증번호 입력'}
        defaultValue={''}
        disabled={!clockOn}
        error={errors.authCode}
        rightElement={
          <ButtonComponent
            bgColor={theme.DF_COLOR.GREEN500}
            borderColor={theme.DF_COLOR.GREEN500}
            textColor={theme.DF_COLOR.WHITE}
            text={'인증하기'}
            onClick={() => onAuth(type, authCode, input)}
            disabled={!authCode || authSuccess}
          />
        }
        rules={{
          minLength: { value: 6, message: '6자 이상 입력해주세요' }
        }}>
        {authSuccess && '인증 완료!'}
      </FormInputComponent>
    </Wrapper>
  );
}

export default VerificationForm;

const Wrapper = styled.div`
  width: 100%;
`;
